/**
 * Flight Details API
 *
 * Network-only functions for fetching flight details.
 * NO Redux imports allowed in this file.
 *
 * Uses common flights API utilities which automatically add:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 * - Query params: sessionId, userId
 */
import type { ViewFlightDetails, FlightDetailsContainerData } from '@contracts';
import { FLIGHTS_ENDPOINTS } from '@/api/endpoints';
import { buildQueryParams, buildHeaders, buildUrl } from '@/api/utils';
import { logger } from '@/shared/utils/logger';

// ============================================
// Error Classes
// ============================================

export class FlightDetailsError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'FlightDetailsError';
  }
}

// ============================================
// Types
// ============================================

export interface FlightDetailsRequestParams {
  crid: string;
  rkey: string;
  sessionId?: string;
  currency?: string;
  language?: string;
  region?: string;
  userId?: string;
}

interface FlightDetailsApiResponse {
  uiComponent: {
    type: string;
    data: {
      flightDetails: ViewFlightDetails | FlightDetailsContainerData;
    };
  };
  uiMetadata: {
    requestId: string;
    conversationId: string;
  };
}

export type FlightDetailsResponse = ViewFlightDetails | FlightDetailsContainerData;

// ============================================
// API Functions
// ============================================

/**
 * Fetch flight details from the API
 * Returns ViewFlightDetails for one-way, FlightDetailsContainerData for round-trip
 *
 * Uses common flights API utilities with automatic:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 * - Query params: sessionId, userId
 */
export async function fetchFlightDetails(
  params: FlightDetailsRequestParams,
): Promise<FlightDetailsResponse> {
  const queryParams = buildQueryParams({
    crid: params.crid,
    rkey: params.rkey,
    currency: params.currency ?? 'INR',
    language: params.language ?? 'eng',
    region: params.region ?? 'in',
  });
  const url = buildUrl(FLIGHTS_ENDPOINTS.FLIGHT_DETAILS, queryParams);
  const headers = buildHeaders();

  logger.warn('[FlightDetailsClient] Fetching flight details:', {
    crid: params.crid,
    rkey: params.rkey,
  });

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      throw new FlightDetailsError(
        `API request failed: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    const apiResponse = (await response.json()) as FlightDetailsApiResponse;

    // Console warn the full API response
    logger.warn('[FlightDetailsAPI] Full API Response:', apiResponse);

    // Extract the flight details from the nested structure
    const flightDetails = apiResponse.uiComponent.data.flightDetails;

    logger.warn('[FlightDetailsAPI] Extracted Flight Details:', flightDetails);
    logger.warn('[FlightDetailsClient] Received flight details:', flightDetails);

    return flightDetails;
  } catch (error) {
    if (error instanceof FlightDetailsError) {
      throw error;
    }

    logger.error('[FlightDetailsClient] Request failed:', error);
    throw new FlightDetailsError('Failed to fetch flight details', undefined, error);
  }
}
